import { useDispatch, useReduxState } from 'hooks';
import { actions } from 'store';
import { breakpoints } from 'styles';
import { getAttributesWithMergedOptions } from 'shared-helpers/customAttributes';
import { attributesExpressionOptions } from 'lib/customAttributes';
import { Button, ModalButtonWithForm } from 'components/common';
import { ArrayField, Select, Text } from 'components/common/form';

import type { AttributeCondition } from 'shared-types';

type FormValues = { conditions: AttributeCondition[] };

interface Props {
  conditions?: any[];
  conditionsIndex?: number;
  disabled?: boolean;
  isEdit?: boolean;
  onSubmit: (newConditions: AttributeCondition[]) => Promise<void>;
}

const GroupUserAttributesAddOrEdit = ({
  conditions,
  conditionsIndex,
  disabled = false,
  isEdit = false,
  onSubmit
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    serverConfig: { isDemoEnvironment, userCustomAttributes },
    user
  } = useReduxState();

  const accountTypeConditions = [
    { label: messages.userAttributesEquals, value: 'equals' },
    { label: messages.userAttributesNotEquals, value: 'not equals' },
    { label: messages.userAttributesIsOneOf, value: 'is one of' }
  ];

  const accountTypeOptions = [
    { label: messages.usersAdmin, value: 'admin' },
    { label: messages.usersSuperadmin, value: 'superadmin' },
    { label: messages.adminSupervisor, value: 'supervisor' },
    { label: messages.commonUser, value: 'user' }
  ];

  const handleAttributeChange = (
    value: string,
    setValue: (
      field: string,
      value: FormValues['conditions'][0]['expression']
    ) => void
  ) => {
    const attribute = userCustomAttributes.find(
      (attribute) => attribute.key === value
    );

    if (attribute?.type === 'Array' || attribute?.type === 'ArrayWithOptions')
      setValue('expression', 'includes');
    else setValue('expression', 'equals');
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      const newConditions = values.conditions.map((condition) => {
        if (
          condition.key === 'emailDomain' &&
          typeof condition.value === 'string' &&
          condition.value.startsWith('@')
        )
          condition.value = condition.value.slice(1);

        return {
          ...condition,
          value:
            typeof condition.value === 'string'
              ? condition.value.trim()
              : condition.value !== undefined
              ? condition.value
              : ''
        };
      });
      await onSubmit(newConditions);
      dispatch(
        actions.addNotice({
          text: isEdit
            ? messages.userAttributesConditionsEditSuccess
            : messages.userAttributesConditionsAddSuccess,
          type: 'success'
        })
      );
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const initialValues: FormValues = {
    conditions: conditions || [
      { expression: 'equals', key: 'emailDomain', value: '' }
    ]
  };

  const viewableUserCustomAttributes = getAttributesWithMergedOptions({
    language,
    onlyViewable: true,
    requestUser: user,
    userCustomAttributes
  });

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonDisabled={disabled || isDemoEnvironment}
      buttonIcon={isEdit ? 'pen' : 'plus'}
      buttonIsDropdown={isEdit}
      buttonSize="header"
      buttonStyle={{ whiteSpace: 'nowrap' }}
      buttonText={
        isEdit ? messages.commonEdit : messages.userAttributesConditionsAdd
      }
      initialValues={initialValues}
      modalActionText={isEdit ? messages.commonSave : messages.commonAdd}
      modalTitle={
        isEdit
          ? messages.userAttributesConditionsEdit
          : messages.userAttributesConditionsAdd
      }
      modalWidth="medium"
      onSubmit={handleSubmit}
      pristineIsValid={false}
    >
      {() => (
        <div className="content">
          <div className="info">{messages.groupsUserAttributesAddInfo}</div>

          <div className="conditions">
            <ArrayField field="conditions">
              {({ add }) => (
                <>
                  <ArrayField.Items<{ expression: string; key: string }>>
                    {({ remove, field, index, values, setValue }) => {
                      const attribute = viewableUserCustomAttributes.find(
                        (attribute) => attribute.key === values?.key
                      );

                      const initialCondition = conditions?.[index];

                      return (
                        <div className="condition">
                          {index !== 0 ? (
                            <div className="condition-pre">
                              {messages.commonAnd}
                            </div>
                          ) : null}

                          <div className="condition-key">
                            <Select
                              field={`${field}.key`}
                              id={`group-user-attributes-add-condition-key-${index}-${conditionsIndex}`}
                              initialValue={
                                initialCondition ? undefined : 'emailDomain'
                              }
                              onChange={(value: string) =>
                                handleAttributeChange(value, setValue)
                              }
                              options={[
                                {
                                  label: messages.userAttributesEmailDomain,
                                  value: 'emailDomain'
                                },
                                ...(user?.roles.includes('superadmin')
                                  ? [
                                      {
                                        label: messages.usersAccountType,
                                        value: 'accountType'
                                      }
                                    ]
                                  : []),
                                ...viewableUserCustomAttributes.map(
                                  (attribute) => ({
                                    label:
                                      attribute.titleTranslations?.[language] ||
                                      attribute.title,
                                    value: attribute.key
                                  })
                                )
                              ]}
                            />
                          </div>

                          <div className="condition-post">
                            <Select
                              field={`${field}.expression`}
                              id={`group-user-attributes-add-condition-expression-${index}-${conditionsIndex}`}
                              initialValue={
                                initialCondition
                                  ? undefined
                                  : attribute?.type === 'Array' ||
                                    attribute?.type === 'ArrayWithOptions'
                                  ? 'includes'
                                  : 'equals'
                              }
                              options={
                                values?.key === 'accountType'
                                  ? accountTypeConditions
                                  : attributesExpressionOptions({
                                      messages,
                                      type: attribute?.type
                                    })
                              }
                              style={{ maxWidth: 150 }}
                            />
                          </div>

                          {['any', 'empty'].includes(
                            values?.expression as string
                          ) ? null : (
                            <div className="condition-value">
                              {values?.key === 'accountType' ? (
                                <Select
                                  field={`${field}.value`}
                                  id={`group-user-attributes-add-condition-value-account-type-${index}-${conditionsIndex}`}
                                  initialValue={
                                    initialCondition
                                      ? undefined
                                      : values?.expression === 'is one of'
                                      ? undefined
                                      : accountTypeOptions[0].value
                                  }
                                  isMulti={values?.expression === 'is one of'}
                                  options={accountTypeOptions}
                                  style={{ minWidth: 140 }}
                                />
                              ) : attribute?.type === 'Boolean' ? (
                                <Select
                                  field={`${field}.value`}
                                  id={`group-user-attributes-add-condition-value-boolean-${index}-${conditionsIndex}`}
                                  initialValue={
                                    initialCondition ? undefined : true
                                  }
                                  options={[
                                    {
                                      label: messages.commonYes,
                                      value: true
                                    },
                                    { label: messages.commonNo, value: false },
                                    {
                                      label:
                                        messages.userAttributesNoIncludingNotSet,
                                      value: 'no including not set'
                                    }
                                  ]}
                                  style={{ minWidth: 150 }}
                                />
                              ) : attribute?.type === 'Number' ? (
                                <Select
                                  field={`${field}.value`}
                                  id={`group-user-attributes-add-condition-value-number-${index}-${conditionsIndex}`}
                                  initialValue={
                                    initialCondition ? undefined : attribute.min
                                  }
                                  isSearchable={false}
                                  options={Array.from(
                                    Array(
                                      Math.max(
                                        attribute.max - attribute.min + 1,
                                        1
                                      )
                                    ),
                                    (_, i) => ({
                                      label: i + attribute.min,
                                      value: i + attribute.min
                                    })
                                  )}
                                />
                              ) : attribute?.type === 'ArrayWithOptions' ||
                                attribute?.type === 'StringWithOptions' ? (
                                <Select
                                  field={`${field}.value`}
                                  id={`group-user-attributes-add-condition-value-options-${index}-${conditionsIndex}`}
                                  initialValue={
                                    initialCondition
                                      ? undefined
                                      : attribute.mergedOptions[0]?.key
                                  }
                                  isMulti={
                                    values?.expression === 'is one of' ||
                                    values?.expression === 'includes one of'
                                  }
                                  isSearchable={true}
                                  options={[
                                    ...attribute.mergedOptions.map(
                                      (option) => ({
                                        label: option.label,
                                        value: option.key
                                      })
                                    )
                                  ]}
                                  showLabelForSelectedElement={true}
                                />
                              ) : (attribute?.type === 'String' ||
                                  attribute?.type === 'Array') &&
                                (values?.expression === 'is one of' ||
                                  values?.expression === 'includes one of') ? (
                                <Select
                                  field={`${field}.value`}
                                  id={`group-user-attributes-add-condition-value-options-${index}-${conditionsIndex}`}
                                  initialValue={
                                    initialCondition ? undefined : []
                                  }
                                  creatable={true}
                                  isMulti={true}
                                  isSearchable={true}
                                  style={{ minWidth: 150 }}
                                />
                              ) : (
                                <Text
                                  initialValue={
                                    initialCondition ? undefined : ''
                                  }
                                  field={`${field}.value`}
                                  required={true}
                                  validateOnChange={true}
                                />
                              )}
                            </div>
                          )}

                          {index !== 0 ? (
                            <div className="condition-actions">
                              <Button
                                aria-label={messages.commonDelete}
                                category="light"
                                data-tooltip={messages.commonDelete}
                                icon="trash-alt"
                                onClick={remove}
                                size="small"
                                style={{ marginTop: 4 }}
                              />
                            </div>
                          ) : null}
                        </div>
                      );
                    }}
                  </ArrayField.Items>

                  <Button
                    category="light"
                    icon="plus"
                    onClick={add}
                    size="small"
                    style={{ marginTop: 10 }}
                    text={messages.userAttributesConditionsAdd}
                  />
                </>
              )}
            </ArrayField>
          </div>

          <style jsx>{`
            .conditions {
              margin-top: 10px;
            }
            .condition {
              align-items: flex-start;
              flex-wrap: wrap;
              display: flex;
              gap: 10px;
            }
            .condition:not(:first-child) {
              margin-top: 10px;
            }
            .condition-pre {
              margin: 9px 0;
              white-space: nowrap;
            }
            .condition-key {
              flex-shrink: 1;
              min-width: 140px;
              width: 200px;
            }
            .condition-post {
              flex-shrink: 0;
              width: 140px;
            }
            .condition-value {
              flex-grow: 1;
            }
            @media (min-width: ${breakpoints.medium}px) {
              .condition {
                flex-wrap: nowrap;
              }
            }
          `}</style>
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default GroupUserAttributesAddOrEdit;
